import { useState, useEffect, useMemo } from 'react';
import { Trash2, FolderPlus, Sparkles, Store, Globe, Tablet, ChefHat, Lock, ArrowUp, ArrowDown } from 'lucide-react';
import { sourozenci } from './poradiKategorii';
import { api } from '../../lib/spizirnaUi';
import RecipeKatIcon, { ICON_MAP, ICON_KEYS } from './RecipeKatIcon';
import RecipeCategoryModal from './RecipeCategoryModal';
import NastaveniPridavku from './NastaveniPridavku';
import UpravyPoTuknuti from './UpravyPoTuknuti';
import PridavkyVCene from './PridavkyVCene';
import StitekPolozky, { zdedenyStitek } from './StitekPolozky';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import NahratMedia from '../NahratMedia';
import { KAT_PALETTE, SECTION_COLOR } from './receptUtils';

const KANALY = [
    { klic: 'viditelne_pokladna', text: 'Pokladna', Icon: Store, barva: '#F59E0B' },
    { klic: 'viditelne_eshop', text: 'E-shop', Icon: Globe, barva: '#3B82F6' },
    { klic: 'viditelne_kiosek', text: 'Kiosek', Icon: Tablet, barva: '#8B5CF6' },
];

const sklonuj = (n, jedna, dve, pet) => `${n} ${n === 1 ? jedna : (n >= 2 && n <= 4 ? dve : pet)}`;

/**
 * Detail kategorie — druhý sloupec receptáře místo modalu (standard §1i).
 *
 * Kategorie nese nastavení, které se dědí na všechno pod ní (viditelnost,
 * kuchyňská stanice, skupiny ingrediencí). Proto se upravuje jako detail:
 * seznam vlevo ukazuje, kterých produktů se změna týká, a třetí sloupec
 * (KategorieDopad) vypíše, kdo má vlastní nastavení.
 *
 * Všechno se ukládá hned — žádné tlačítko Uložit, které by na půlce záložek
 * nic nedělalo. Název po opuštění pole nebo Enteru.
 */
export default function KategorieDetail({
    node, typ, flatKat, pocty, isDark, isMobile, cardStyle, mutedColor,
    onCatSave, onCatAdd, onCatDelete, onAiAnalyza, onZavrit,
    pridavkyKlic = 0, onZmenaPridavku,
    // Pořadí mezi sourozenci (šipky Výš / Níž); bez onPosun se nekreslí.
    onPosun = null, strom = null,
}) {
    const [zalozka, setZalozka] = useState('obecne');
    const [nazev, setNazev] = useState(node.nazev || '');
    const [stanice, setStanice] = useState([]);
    const [podkategorie, setPodkategorie] = useState(false);

    const [hlaska, setHlaska] = useState(node.eshop_hlaska || '');
    const [nadpisEshop, setNadpisEshop] = useState(node.eshop_nadpis || '');

    useEffect(() => { setNazev(node.nazev || ''); }, [node.id, node.nazev]);
    useEffect(() => { setHlaska(node.eshop_hlaska || ''); }, [node.id, node.eshop_hlaska]);
    useEffect(() => { setNadpisEshop(node.eshop_nadpis || ''); }, [node.id, node.eshop_nadpis]);
    useEffect(() => { setZalozka(z => ((z === 'ingredience' || z === 'combo' || z === 'eshop') && typ !== 'PRO' ? 'obecne' : z)); }, [typ]);
    useEffect(() => {
        api('/api/kds-stanice').then(d => setStanice((d || []).filter(x => x.aktivni))).catch(() => {});
    }, []);

    const border = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const fieldBg = isDark ? 'rgba(255,255,255,0.06)' : '#fff';

    // Předci od rodiče ke kořeni — kvůli efektivní viditelnosti a stanici.
    const predci = useMemo(() => {
        const podleId = new Map((flatKat || []).map(k => [k.id, k]));
        const out = [];
        let p = node.parent_id ? podleId.get(node.parent_id) : null;
        while (p && out.length < 20) { out.push(p); p = p.parent_id ? podleId.get(p.parent_id) : null; }
        return out;
    }, [flatKat, node.parent_id]);

    const uloz = (patch) => onCatSave(node.id, patch);
    const ulozNazev = () => {
        const v = nazev.trim();
        if (v && v !== node.nazev) uloz({ nazev: v }); else setNazev(node.nazev || '');
    };

    const zalozky = [
        { id: 'obecne', text: 'Obecné' },
        { id: 'viditelnost', text: 'Viditelnost' },
        // Sklad: kuchyňská stanice a štítek na krabici (2026-10-23).
        ...(stanice.length > 1 || typ === 'PRO' ? [{ id: 'sklad', text: 'Sklad' }] : []),
        ...(typ === 'PRO' ? [{ id: 'ingredience', text: 'Ingredience' }, { id: 'combo', text: 'Combo' }, { id: 'eshop', text: 'E-shop' }] : []),
    ];

    const label = (text) => (
        <div className="text-[10px] font-semibold uppercase mb-1.5" style={{ color: mutedColor, letterSpacing: '0.05em' }}>{text}</div>
    );
    const autoBtn = (onClick, active) => (
        <button type="button" onClick={onClick} title="Automaticky (dle názvu)"
            className="text-[10px] px-2 py-0.5 rounded"
            style={{ color: active ? SECTION_COLOR : mutedColor, border: `1px solid ${active ? SECTION_COLOR : border}`, background: 'none', cursor: 'pointer', height: 24 }}>
            auto
        </button>
    );

    const skrytaPredkem = (klic) => predci.find(p => !p[klic]);
    const zdedenaStanice = predci.find(p => p.kds_stanice_id)?.kds_stanice_id ?? null;
    const zdedeneUpravy = predci.find(p => p.pos_upravy != null)?.pos_upravy ?? false;
    const zdedeneVCene = predci.find(p => p.pridavky_v_cene != null)?.pridavky_v_cene ?? 0;
    const nazevStanice = (id) => stanice.find(s => s.id === id)?.nazev || 'Kuchyně 1';

    const hlavicka = [
        node.parent_id && predci[0] ? `podkategorie ${predci[0].nazev}` : 'kořenová kategorie',
        sklonuj(pocty.produktu, 'produkt', 'produkty', 'produktů'),
        pocty.velikosti !== pocty.produktu ? sklonuj(pocty.velikosti, 'velikost', 'velikosti', 'velikostí') : null,
        pocty.podkategorii ? sklonuj(pocty.podkategorii, 'podkategorie', 'podkategorie', 'podkategorií') : null,
    ].filter(Boolean);

    return (
        <div className="rounded-xl overflow-hidden flex flex-col flex-1" style={cardStyle}>
            <div className="flex items-start gap-3 p-4" style={{ borderBottom: `1px solid ${border}` }}>
                {isMobile && <ZpetTlacitko onClick={onZavrit} popis="Zpět na seznam" />}
                <RecipeKatIcon slozka ikona={node.ikona} kategorieNazev={node.nazev} kategorieBarva={node.barva} size={58} radius={10} />
                <div className="min-w-0 flex-1">
                    <input value={nazev} onChange={e => setNazev(e.target.value)} onBlur={ulozNazev}
                        onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setNazev(node.nazev || ''); } }}
                        aria-label="Název kategorie"
                        className="text-lg font-bold w-full rounded-md px-1 -ml-1"
                        style={{ background: 'transparent', border: '1px solid transparent', color: 'var(--text-primary)', outline: 'none' }}
                        onFocus={e => { e.currentTarget.style.borderColor = border; }} />
                    <div className="flex flex-wrap gap-1.5 mt-1">
                        {hlavicka.map(t => (
                            <span key={t} className="text-[11px] px-2 py-0.5 rounded-md" style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)', color: 'var(--text-secondary)' }}>{t}</span>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                    {onAiAnalyza && (
                        <button type="button" onClick={() => onAiAnalyza(node)} title="AI analýza kategorie"
                            className="flex items-center justify-center rounded-lg"
                            style={{ width: 34, height: 34, background: 'none', border: `1px solid ${border}`, cursor: 'pointer', color: '#A855F7' }}>
                            <Sparkles size={15} />
                        </button>
                    )}
                    <button type="button" onClick={() => setPodkategorie(true)} title="Nová podkategorie"
                        className="flex items-center justify-center rounded-lg"
                        style={{ width: 34, height: 34, background: 'none', border: `1px solid ${border}`, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                        <FolderPlus size={15} />
                    </button>
                    {!isMobile && (
                        <button type="button" title="Smazat kategorii"
                            onClick={() => {
                                if (confirm(`Smazat kategorii „${node.nazev}"? Podkategorie se přesunou o úroveň výš.`)) { onCatDelete(node.id); onZavrit(); }
                            }}
                            className="flex items-center justify-center rounded-lg"
                            style={{ width: 34, height: 34, background: 'none', border: `1px solid ${border}`, cursor: 'pointer', color: '#EF4444' }}>
                            <Trash2 size={15} />
                        </button>
                    )}
                </div>
            </div>

            <div role="tablist" className="flex gap-5 px-4 overflow-x-auto" style={{ borderBottom: `1px solid ${border}` }}>
                {zalozky.map(t => (
                    <button key={t.id} type="button" role="tab" aria-selected={zalozka === t.id} onClick={() => setZalozka(t.id)}
                        className="text-sm font-medium py-2.5 whitespace-nowrap"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', marginBottom: -1,
                            borderBottom: `2px solid ${zalozka === t.id ? SECTION_COLOR : 'transparent'}`,
                            color: zalozka === t.id ? SECTION_COLOR : 'var(--text-secondary)' }}>
                        {t.text}
                    </button>
                ))}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {zalozka === 'obecne' && (
                    <div className="space-y-5" style={{ maxWidth: 560 }}>
                        <div>
                            {label('Barva')}
                            <div className="flex flex-wrap items-center gap-1.5">
                                {KAT_PALETTE.map(c => (
                                    <button key={c} type="button" onClick={() => uloz({ barva: c })} title={c} aria-label={`Barva ${c}`}
                                        style={{ width: 26, height: 26, borderRadius: 6, background: c, cursor: 'pointer',
                                            border: node.barva === c ? '2px solid var(--text-primary)' : '1px solid rgba(0,0,0,0.15)' }} />
                                ))}
                                {autoBtn(() => uloz({ barva: null }), node.barva == null)}
                            </div>
                        </div>
                        <div>
                            {label('Ikona')}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(42px, 1fr))', gap: 6 }}>
                                {ICON_KEYS.map(key => {
                                    const Icon = ICON_MAP[key];
                                    const on = node.ikona === key;
                                    return (
                                        <button key={key} type="button" onClick={() => uloz({ ikona: key })} title={key} aria-label={`Ikona ${key}`}
                                            className="rounded-lg flex items-center justify-center"
                                            style={{ aspectRatio: '1', cursor: 'pointer',
                                                background: on ? 'rgba(245,158,11,0.15)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                                                border: `1px solid ${on ? SECTION_COLOR : border}` }}>
                                            <Icon size={17} style={{ color: on ? SECTION_COLOR : 'var(--text-primary)' }} />
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-2">{autoBtn(() => uloz({ ikona: null }), node.ikona == null)}</div>
                        </div>
                        {onPosun && (() => {
                            const s = sourozenci(strom, node.id);
                            if (!s || s.seznam.length < 2) return null;
                            const i = s.seznam.findIndex(n => n.id === node.id);
                            const tl = (smer, Ikona, text, vypnuto) => (
                                <button type="button" onClick={() => onPosun(smer)} disabled={vypnuto}
                                    className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium"
                                    style={{ background: fieldBg, border: `1px solid ${border}`, color: vypnuto ? mutedColor : 'var(--text-primary)', cursor: vypnuto ? 'default' : 'pointer', opacity: vypnuto ? 0.5 : 1 }}>
                                    <Ikona size={14} /> {text}
                                </button>
                            );
                            return (
                                <div>
                                    {label('Pořadí')}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {tl(-1, ArrowUp, 'Výš', i === 0)}
                                        {tl(1, ArrowDown, 'Níž', i === s.seznam.length - 1)}
                                        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{i + 1}. z {s.seznam.length}{node.parent_id ? ' v kategorii' : ' hlavních kategorií'}</span>
                                    </div>
                                    <p className="text-[11px] mt-1.5" style={{ color: mutedColor }}>
                                        Pořadí platí pro pokladnu, kiosek i e-shop. Ve stromu jde kategorie přetáhnout nad nebo pod jinou — ukáže se oranžová čára.
                                    </p>
                                </div>
                            );
                        })()}
                        <p className="text-[11px]" style={{ color: mutedColor }}>Změny se ukládají hned.</p>
                    </div>
                )}

                {zalozka === 'viditelnost' && (
                    <div className="space-y-2" style={{ maxWidth: 560 }}>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                            Kam se kategorie propíše. Admin ji vidí vždy. Skrytá kategorie skryje i všechno pod sebou.
                        </p>
                        {KANALY.map(({ klic, text, Icon, barva }) => {
                            const on = !!node[klic];
                            const predek = skrytaPredkem(klic);
                            return (
                                <button key={klic} type="button" onClick={() => uloz({ [klic]: !on })}
                                    className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left"
                                    style={{ background: fieldBg, border: `1px solid ${on ? barva : border}`, cursor: 'pointer' }}>
                                    <Icon size={16} style={{ color: on ? barva : mutedColor, flexShrink: 0 }} />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm" style={{ color: 'var(--text-primary)' }}>{text}</span>
                                        {on && predek && (
                                            <span className="flex items-center gap-1 text-[11px]" style={{ color: '#F59E0B' }}>
                                                <Lock size={10} /> skryto nadřazenou kategorií {predek.nazev}
                                            </span>
                                        )}
                                    </span>
                                    <span style={{ width: 34, height: 20, borderRadius: 999, flexShrink: 0, position: 'relative', background: on ? barva : (isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)') }}>
                                        <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.15s' }} />
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {zalozka === 'sklad' && (
                    <div className="space-y-4" style={{ maxWidth: 640 }}>
                        {typ === 'PRO' && (
                            <StitekPolozky
                                value={{ stitek: node.stitek ?? null, stitek_skupina: node.stitek_skupina ?? null }}
                                zdedeno={zdedenyStitek(predci)}
                                nazevZdedeni={node.parent_id ? 'Zdědit' : 'Výchozí'}
                                navrhySkupin={(flatKat || []).map(k => k.stitek_skupina)}
                                isDark={isDark}
                                onChange={v => uloz(v)} />
                        )}
                        {stanice.length > 1 && (<div>
                        <div className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Kuchyňská stanice</div>
                        <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                            Na kterém kuchyňském displeji se položky kategorie zobrazí. Podkategorie a produkty to dědí, u produktu jde přebít.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {[{ id: null, nazev: `Zdědit${node.parent_id ? ` (${nazevStanice(zdedenaStanice)})` : ' (Kuchyně 1)'}` }, ...stanice].map(s => {
                                const on = (node.kds_stanice_id ?? null) === s.id;
                                return (
                                    <button key={s.id ?? 'zdedit'} type="button" onClick={() => uloz({ kds_stanice_id: s.id })}
                                        className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium"
                                        style={{ background: on ? 'rgba(245,158,11,0.15)' : fieldBg, border: `1px solid ${on ? SECTION_COLOR : border}`, color: on ? SECTION_COLOR : 'var(--text-primary)', cursor: 'pointer' }}>
                                        {s.id ? <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.barva || mutedColor }} /> : <ChefHat size={12} />}
                                        {s.nazev}
                                    </button>
                                );
                            })}
                        </div>
                        </div>)}
                    </div>
                )}

                {zalozka === 'ingredience' && (
                    <div className="space-y-4">
                        <UpravyPoTuknuti value={node.pos_upravy ?? null} zdedeno={zdedeneUpravy} isDark={isDark}
                            nazevZdedeni={node.parent_id ? 'Zdědit' : 'Výchozí'}
                            onChange={v => uloz({ pos_upravy: v })} />
                        <PridavkyVCene value={node.pridavky_v_cene ?? null} zdedeno={zdedeneVCene} isDark={isDark}
                            nazevZdedeni={node.parent_id ? 'Zdědit' : 'Výchozí'}
                            onChange={v => uloz({ pridavky_v_cene: v })} />
                        <NastaveniPridavku typ="kategorie" id={node.id} isDark={isDark}
                            obnovit={pridavkyKlic} onZmena={onZmenaPridavku} />
                    </div>
                )}

                {zalozka === 'eshop' && (
                    <div className="space-y-5" style={{ maxWidth: 560 }}>
                        {node.parent_id && (
                            <p className="text-xs rounded-lg px-3 py-2" style={{ background: 'rgba(245,158,11,0.1)', color: '#F59E0B' }}>
                                Dlaždice v menu e-shopu mají jen hlavní kategorie. U podkategorie se obrázek nezobrazí.
                            </p>
                        )}
                        <div>
                            {label('Obrázek dlaždice v menu')}
                            <div className="flex items-start gap-4 flex-wrap">
                                {/* Náhled tak, jak dlaždice vypadá na webu: jídlo přesahuje horní hranu. */}
                                <div aria-hidden="true" style={{ width: 132, flexShrink: 0, paddingTop: 34 }}>
                                    <div style={{ position: 'relative', height: 112, borderRadius: 16, background: 'linear-gradient(160deg, #2b2730, #1c1a1f)', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 12 }}>
                                        {node.eshop_foto && node.eshop_foto_vyrez && (
                                            <img src={node.eshop_foto} alt="" style={{ position: 'absolute', left: '50%', bottom: 45, width: '90%', height: 106, objectFit: 'contain', objectPosition: 'center bottom', transform: 'translateX(-50%)', filter: 'drop-shadow(0 8px 10px rgba(0,0,0,0.45))' }} />
                                        )}
                                        {node.eshop_foto && !node.eshop_foto_vyrez && (
                                            <img src={node.eshop_foto} alt="" style={{ position: 'absolute', left: '17%', bottom: 45, width: '66%', height: 66, objectFit: 'cover', borderRadius: 12, boxShadow: '0 10px 18px -8px rgba(0,0,0,0.7)' }} />
                                        )}
                                        {!node.eshop_foto
                                            && <span style={{ position: 'absolute', top: 18, left: 0, right: 0, textAlign: 'center', fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>bez obrázku</span>}
                                        <b style={{ position: 'relative', color: '#fff', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.02em', textAlign: 'center', lineHeight: 1.05, padding: '0 6px' }}>{node.nazev}</b>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 flex-1" style={{ minWidth: 220 }}>
                                    <NahratMedia isDark={isDark} accept="image/png,image/webp,image/*" vyska={84} accent={SECTION_COLOR}
                                        popisek="Přetáhni obrázek nebo vyber z počítače"
                                        onNahrano={(soubor) => uloz({ eshop_media_soubor_id: soubor.id })} />
                                    {node.eshop_media_soubor_id && (
                                        <button type="button" onClick={() => uloz({ eshop_media_soubor_id: null, eshop_foto: null })}
                                            className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium"
                                            style={{ background: 'none', border: `1px solid ${border}`, color: '#EF4444', cursor: 'pointer' }}>
                                            <Trash2 size={13} /> Odstranit obrázek
                                        </button>
                                    )}
                                    <Pozadavky mutedColor={mutedColor} polozky={[
                                        'Vyřezané jídlo na průhledném pozadí — PNG nebo WebP s alfa kanálem.',
                                        'Čtverec 1000 × 1000 px (stačí od 600 px), do 5 MB.',
                                        'Jídlo z mírného nadhledu (asi 30°), celé v záběru, nic neuříznuté.',
                                        'Bez textu, loga, talíře přes okraj a bez stínu na pozadí — stín přidá web.',
                                        'Průhledný okraj se ořízne sám. Použije se i jako malá ikona v liště kategorií.',
                                    ]} />
                                </div>
                            </div>
                        </div>
                        <div>
                            {label('Nadpis sekce')}
                            <input value={nadpisEshop} onChange={e => setNadpisEshop(e.target.value)} maxLength={80}
                                onBlur={() => { const v = nadpisEshop.trim(); if (v !== (node.eshop_nadpis || '')) uloz({ eshop_nadpis: v || null }); }}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                placeholder={`Např. Naše ${(node.nazev || '').toLowerCase()}`}
                                className="w-full rounded-lg px-3 py-2 text-sm"
                                style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', outline: 'none' }} />
                            <p className="text-[11px] mt-1.5" style={{ color: mutedColor }}>Velký nadpis sekce v menu. Prázdné = název kategorie.</p>
                        </div>
                        <div>
                            {label('Podtitulek sekce')}
                            <input value={hlaska} onChange={e => setHlaska(e.target.value)} maxLength={160}
                                onBlur={() => { const v = hlaska.trim(); if (v !== (node.eshop_hlaska || '')) uloz({ eshop_hlaska: v || null }); }}
                                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                placeholder="Např. Pořádná porce. Žádné kompromisy."
                                className="w-full rounded-lg px-3 py-2 text-sm"
                                style={{ background: fieldBg, border: `1px solid ${border}`, color: 'var(--text-primary)', outline: 'none' }} />
                            <p className="text-[11px] mt-1.5" style={{ color: mutedColor }}>Krátká věta pod nadpisem kategorie v menu e-shopu.</p>
                        </div>
                        <div>
                            {label('Obrázek záhlaví sekce')}
                            {/* Náhled: pruh za nadpisem, obrázek vpravo, vlevo a dole splývá s pozadím. */}
                            <div aria-hidden="true" style={{ position: 'relative', aspectRatio: '8 / 3', borderRadius: 12, overflow: 'hidden', marginBottom: 10,
                                background: '#151214', border: `1px solid ${border}` }}>
                                {node.eshop_zahlavi && (
                                    <img src={node.eshop_zahlavi} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'right center',
                                        WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, #000 45%), linear-gradient(0deg, transparent 0%, #000 30%)', WebkitMaskComposite: 'source-in',
                                        maskImage: 'linear-gradient(90deg, transparent 0%, #000 45%), linear-gradient(0deg, transparent 0%, #000 30%)', maskComposite: 'intersect' }} />
                                )}
                                <div style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)' }}>
                                    <div style={{ color: '#fff', fontWeight: 800, fontSize: 20, lineHeight: 1 }}>{nadpisEshop || node.nazev}</div>
                                    {hlaska && <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, fontStyle: 'italic', marginTop: 4 }}>{hlaska}</div>}
                                </div>
                                {!node.eshop_zahlavi && <span style={{ position: 'absolute', right: 14, bottom: 10, fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>bez obrázku záhlaví</span>}
                            </div>
                            <div className="flex flex-col gap-2">
                                <NahratMedia isDark={isDark} accept="image/webp,image/png,image/jpeg,image/*" vyska={72} accent={SECTION_COLOR}
                                    popisek="Přetáhni obrázek záhlaví nebo vyber z počítače"
                                    onNahrano={(soubor) => uloz({ eshop_zahlavi_media_soubor_id: soubor.id })} />
                                {node.eshop_zahlavi_media_soubor_id && (
                                    <button type="button" onClick={() => uloz({ eshop_zahlavi_media_soubor_id: null, eshop_zahlavi: null })}
                                        className="flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium"
                                        style={{ background: 'none', border: `1px solid ${border}`, color: '#EF4444', cursor: 'pointer' }}>
                                        <Trash2 size={13} /> Odstranit záhlaví
                                    </button>
                                )}
                                <Pozadavky mutedColor={mutedColor} polozky={[
                                    'Široký pruh 1600 × 600 px (poměr 8 : 3), WebP nebo JPG, do 5 MB.',
                                    'Motiv (jídlo) v pravé třetině až polovině, levých 45 % tmavých a klidných — je tam nadpis.',
                                    'Tmavé teplé pozadí blízké #151214, bez ostrého okraje — web obrázek vlevo a dole rozmaže do stránky.',
                                    'Bez textu a loga. Na mobilu se ukáže jen pravá část, ztlumeně za nadpisem.',
                                ]} />
                            </div>
                        </div>
                        <p className="text-[11px]" style={{ color: mutedColor }}>Změny se ukládají hned. Web je ukáže do minuty.</p>
                    </div>
                )}

                {zalozka === 'combo' && (
                    <NastaveniPridavku druh="combo" typ="kategorie" id={node.id} isDark={isDark} />
                )}
            </div>

            {podkategorie && (
                <RecipeCategoryModal
                    state={{ mode: 'create', parentId: node.id, parentNazev: node.nazev }}
                    isDark={isDark}
                    onClose={() => setPodkategorie(false)}
                    onCreate={(parentId, data) => onCatAdd(parentId, data)}
                    onSave={() => {}} onDelete={() => {}} onAddSub={() => {}}
                    zobrazitViditelnost
                />
            )}
        </div>
    );
}

/** Odrážky s požadavky na obrázek pod nahráváním. */
function Pozadavky({ polozky, mutedColor }) {
    return (
        <ul className="text-[11px] space-y-0.5" style={{ color: mutedColor, listStyle: 'disc', paddingLeft: 16 }}>
            {polozky.map(p => <li key={p}>{p}</li>)}
        </ul>
    );
}
